import React, {type JSX} from 'react';
import {isDevelopmentStage} from 'src/utils/environment';
import {DevErrorDisplay} from 'src/theming/components/DevErrorDisplay';

interface PropsType {
  children: JSX.Element;
}

interface StateType {
  hasError: boolean;
  error: Error | null;
  errorInfo: React.ErrorInfo | null;
}

/**
 * Simple class to manage the application initialization metric.
 *
 * Note this is meant as a demonstration of how to use the initialization metric, and
 * is almost certainly inadequate for a more complex application.
 *
 * It waits for itself to be mounted (if it is the outermost component that means all of
 * its children are rendered and mounted by React, though are not necessarily fully rendered
 * in the browser), then waits for the translations to be ready.
 *
 * In your actual application, you are likely not fully initialized until you have completed
 * your initial REST calls.  You will need to either integrate that logic here, or else move
 * the initialization metric elsewhere.
 *
 * For applications using a state manager (e.g. Redux) it may make more sense to determine if
 * your application is initialized from that layer instead.
 */
export class AppInitWrapper extends React.Component<PropsType, StateType> {
  constructor(props: PropsType) {
    super(props);
    this.state = {
      hasError: false,
      error: null,
      errorInfo: null,
    };
  }

  componentDidMount() {
    // Publish component initialization metrics
  }

  componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
    // Log the error regardless of stage
    console.error('AppInitWrapper caught an error:', error, errorInfo);

    // For development stages (alpha/beta), show error display in UI
    if (isDevelopmentStage()) {
      this.setState({
        hasError: true,
        error,
        errorInfo,
      });
    } else {
      // For production stages, re-throw and fail the whole app
      throw error;
    }
  }

  handleDismissError = () => {
    this.setState({
      hasError: false,
      error: null,
      errorInfo: null,
    });
  };

  render() {
    if (this.state.hasError && this.state.error) {
      return (
        <>
          {this.props.children}
          <DevErrorDisplay
            error={this.state.error}
            errorInfo={this.state.errorInfo || undefined}
            onDismiss={this.handleDismissError}
          />
        </>
      );
    }

    return this.props.children;
  }
}
